import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import apiCodes from './apiCode';
import { ROUTES } from '../route/RouteConstant';
import { decrypt } from '../utils/decrypt';
import Cookies from 'js-cookie';
import logger from './../utils/logger';
import { handleLogout } from '../utils/logout';
import { getUserIdFromJwt } from '../utils/cookiesUtils';
import { SIDEBAR_PERMISSION_STATUS, TOAST_ERROR_MESSAGES } from '../constant/stringConstant';
import apiUrls from './apiUrls';
import { performanceMonitor } from '../utils/performanceMonitor';
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY;

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  decrypt?: boolean;
  /** Unique request ID for deduplication */
  requestId?: string;
  /** Start time for performance tracking */
  startTime?: number;
}
export interface ApiResponse<T> {
  Data: T;
  Message?: string;
  Success?: boolean;
  [key: string]: any;
}

// ✅ Importing environment variable
const BASE_URL = import.meta.env.VITE_BASE_URL

const api = axios.create({
  baseURL: `${BASE_URL}`,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // Note: 'Connection: keep-alive' is automatically handled by browsers
    // We cannot set it manually - browsers control this header
  },
  // Enable HTTP adapter optimizations
  maxRedirects: 5,
  // Decompress responses automatically
  decompress: true,
});

// ✅ Get dynamic userId and token from Redux and storage (sync for performance)
const getHeaderConfig = (): Record<string, string | number> => {
  const jwtToken = Cookies.get("husk.Jwttoken") || "";
  const userId = getUserIdFromJwt(jwtToken) || '';
  return {
    'userId': userId ?? 0,
    'husk-Jwttoken': jwtToken,
  };
};

// 🔁 Request Interceptor (sync for better performance)
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig<any>) => {
    const defaultConfigs = getHeaderConfig();
    const requestHeader = config.headers ?? {};

    config.headers = {
      ...defaultConfigs,
      ...requestHeader,
    } as any;

    // Add performance tracking
    (config as CustomAxiosRequestConfig).startTime = performance.now();

    return config;
  },
  (error) => {
    logger.error('Request Error:', error);
    toast.error('Failed to send request.');
    return Promise.reject(error);
  }
);

// 🔁 Response Interceptor (same as before)
api.interceptors.response.use(
  (response) => {
    const config = response.config as CustomAxiosRequestConfig;

    // Track API performance
    if (config.startTime) {
      performanceMonitor.trackApiCall(config.url || 'unknown', config.startTime);
    }

    // ✅ Default to true unless explicitly false:
    const shouldDecrypt = config.decrypt !== false;

    if (response.status === apiCodes.SUCCESS) {
      const originalData = response.data?.Data ?? response.data?.data;
      response.data.Data = shouldDecrypt
        ? decrypt(originalData)
        : originalData;
    }

    return response.data;
  },

  (error) => {
    const status = error?.response?.status;
    if (error.code === apiCodes.REQUEST_TIME_OUT) {
      toast.error(TOAST_ERROR_MESSAGES.SERVER_TIMEOUT);
    } else if (status === apiCodes.UNAUTHORIZE_REQUEST) {

      if (Cookies.get("husk.Jwttoken") || "") {
        handleLogout();
      }
      return error;
    } else if (status === apiCodes.FORBIDDEN) {
      window.location.replace(ROUTES.FORBIDDEN);
    }
    else if (status === apiCodes.RESOURCE_NOT_FOUND) {
      toast.error(error.response.data.data.message);
    } else if (status === apiCodes.INTERNAL_SERVER_ERROR) {
      const url = error?.config?.url || '';
      // Show toast only if NOT HandlePermission AND NOT CalculateRechargeAmount
      if (!url.includes(apiUrls.HandlePermission) && !url.includes(apiUrls.CalculateRechargeAmount)) {
        toast.error(error?.response?.data?.data?.message || SIDEBAR_PERMISSION_STATUS.ERROR);
      }
      return Promise.reject(error);
    } else {
      // Suppress generic toast for login/resend OTP errors, let viewmodel handle it
      const url = error?.config?.url || '';
      if (!url.includes(apiUrls.ResendOTP)) {
        toast.error(error.response?.data?.data?.message);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
